import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { AGREEXA_APP_URL } from '../lib/links';
import { Sparkles, CheckCircle2, Clock, DollarSign, Wrench, FileText, Send } from 'lucide-react';
export function AIGeneratorSection() {
  const [prompt, setPrompt] = useState('');
  return <section className="py-24 bg-white dark:bg-navy-800 relative overflow-hidden" data-id="element-4">
      {/* Decorative background elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-5">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-orange-500/5 blur-[120px]" data-id="element-6" />
        <div className="absolute bottom-[10%] -left-[10%] w-[40%] h-[40%] rounded-full bg-forest-500/5 blur-[100px]" data-id="element-7" />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10" data-id="element-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-9">
          {/* Left Content */}
          <div data-id="element-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-100 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400 text-sm font-semibold mb-6" data-id="element-11">
              <Sparkles size={16} data-id="element-12" /> AI Project Generator
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-navy-900 dark:text-white mb-6 leading-tight" data-id="element-13">
              Describe your idea. <br data-id="element-14" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-forest-700 to-forest-500 dark:from-forest-400 dark:to-green-300" data-id="element-15">
                We'll build the plan.
              </span>
            </h2>
            <p className="text-lg text-navy-700 dark:text-gray-300 mb-8" data-id="element-16">
              Turn any concept into a fully actionable project. Our AI analyzes
              your idea and generates a comprehensive blueprint in seconds.
            </p>

            <div className="grid sm:grid-cols-2 gap-4 mb-10" data-id="element-17">
              {[{
              icon: FileText,
              text: 'Step-by-step Instructions'
            }, {
              icon: Wrench,
              text: 'Complete Tool List'
            }, {
              icon: DollarSign,
              text: 'Budget Estimation'
            }, {
              icon: Clock,
              text: 'Time Requirements'
            }].map((feature, i) => <div key={i} className="flex items-center gap-3 text-navy-800 dark:text-gray-200" data-id="element-18">
                  <div className="w-8 h-8 rounded-full bg-forest-100 dark:bg-forest-900/50 flex items-center justify-center text-forest-700 dark:text-forest-400" data-id="element-19">
                    <feature.icon size={16} data-id="element-20" />
                  </div>
                  <span className="font-medium" data-id="element-21">{feature.text}</span>
                </div>)}
            </div>

            {/* Interactive Input */}
            <div className="relative max-w-xl" data-id="element-22">
              <div className="absolute inset-0 bg-gradient-to-r from-orange-500 to-forest-500 rounded-2xl blur-lg opacity-20 dark:opacity-30" data-id="element-23" />
              <div className="relative glass-card rounded-2xl p-2 flex items-center" data-id="element-24">
                <input type="text" value={prompt} onChange={e => setPrompt(e.target.value)} placeholder="e.g., Build a smart mirror with a wooden frame..." className="w-full bg-transparent border-none outline-none px-4 py-3 text-navy-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400" data-id="element-25" />
                <button onClick={() => {
                window.location.href = AGREEXA_APP_URL;
              }} className="p-3 rounded-xl bg-navy-900 dark:bg-white text-white dark:text-navy-900 hover:scale-105 transition-transform" data-id="element-26">
                  <Send size={20} data-id="element-27" />
                </button>
              </div>
            </div>
          </div>

          {/* Right Content - Mockup */}
          <motion.div initial={{
          opacity: 0,
          x: 50
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="relative" data-id="element-28">
            <div className="glass-card rounded-3xl p-6 md:p-8 shadow-2xl border-t border-l border-white/40 dark:border-white/10" data-id="element-29">
              <div className="flex items-center justify-between mb-8 pb-6 border-b border-gray-200 dark:border-navy-700" data-id="element-30">
                <div data-id="element-31">
                  <h3 className="text-xl font-bold text-navy-900 dark:text-white mb-1" data-id="element-32">
                    Smart Mirror Build
                  </h3>
                  <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400" data-id="element-33">
                    <span className="flex items-center gap-1" data-id="element-34">
                      <Clock size={14} data-id="element-35" /> Weekend
                    </span>
                    <span className="flex items-center gap-1" data-id="element-36">
                      <DollarSign size={14} data-id="element-37" /> $150 - $200
                    </span>
                  </div>
                </div>
                <div className="px-3 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-bold uppercase tracking-wide" data-id="element-38">
                  Intermediate
                </div>
              </div>

              <div className="space-y-6" data-id="element-39">
                <div data-id="element-40">
                  <h4 className="font-semibold text-navy-900 dark:text-white mb-3 flex items-center gap-2" data-id="element-41">
                    <span className="w-6 h-6 rounded-full bg-navy-100 dark:bg-navy-700 flex items-center justify-center text-xs" data-id="element-42">
                      1
                    </span>
                    Materials Needed
                  </h4>
                  <ul className="space-y-2 text-sm text-navy-700 dark:text-gray-300 ml-8" data-id="element-43">
                    <li className="flex items-center gap-2" data-id="element-44">
                      <CheckCircle2 size={14} className="text-forest-500" data-id="element-45" />{' '}
                      Two-way glass mirror (18" x 24")
                    </li>
                    <li className="flex items-center gap-2" data-id="element-46">
                      <CheckCircle2 size={14} className="text-forest-500" data-id="element-47" />{' '}
                      Raspberry Pi 4 Model B
                    </li>
                    <li className="flex items-center gap-2" data-id="element-48">
                      <CheckCircle2 size={14} className="text-forest-500" data-id="element-49" /> 24"
                      LED Monitor (de-bezeled)
                    </li>
                    <li className="flex items-center gap-2" data-id="element-50">
                      <CheckCircle2 size={14} className="text-forest-500" data-id="element-51" /> 1x4
                      Pine wood for frame
                    </li>
                  </ul>
                </div>

                <div className="p-4 rounded-xl bg-orange-50 dark:bg-orange-900/10 border border-orange-100 dark:border-orange-900/30" data-id="element-52">
                  <h4 className="font-semibold text-orange-800 dark:text-orange-400 mb-2 text-sm" data-id="element-53">
                    Safety Tip
                  </h4>
                  <p className="text-sm text-orange-700 dark:text-orange-300/80" data-id="element-54">
                    When de-bezeling the monitor, ensure it is completely
                    unplugged and discharged to avoid electrical shock.
                  </p>
                </div>
              </div>
            </div>

            {/* Floating elements */}
            <motion.div animate={{
            y: [0, -10, 0]
          }} transition={{
            repeat: Infinity,
            duration: 4,
            ease: 'easeInOut'
          }} className="absolute -right-6 -top-6 glass-card p-4 rounded-2xl shadow-xl" data-id="element-55">
              <div className="flex items-center gap-3" data-id="element-56">
                <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center text-blue-600 dark:text-blue-400" data-id="element-57">
                  <FileText size={20} data-id="element-58" />
                </div>
                <div data-id="element-59">
                  <div className="text-sm font-bold text-navy-900 dark:text-white" data-id="element-60">
                    Blueprint Ready
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-61">PDF Download</div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>;
}